// ─────────────────────────────────────────────────────────────────────────────
// loadingAnimations.js
//
// Single registry of every loading animation the app ships with. Anything
// that displays a "we're working on it" affordance -- HelixLoader, route
// transitions, generation tiles, button spinners with character -- pulls
// from this list rather than hard-coding a path.
//
// Surface-aware defaults:
//   - Timeline tiles (16:9, small):  felix-cyan GIF -- compact, punchy
//   - Node canvas (square, larger):  felix-darkblue GIF -- subtle, ambient
//   - Generic / fallback:            felix-color GIF -- full-color default
//
// The helix-dancing GIF is kept as a legacy fallback only.
//
// Usage:
//   import { getLoadingAnimation, getAnimationForSurface } from '../utils/loadingAnimations';
//   <HelixLoader />                           // uses default (felix-color)
//   <HelixLoader surface="timeline" />        // felix-cyan for timeline tiles
//   <HelixLoader surface="canvas" />          // felix-darkblue for node cards
//   <HelixLoader animationId="random" />      // pick a random one each mount
// ─────────────────────────────────────────────────────────────────────────────

export const LOADING_ANIMATIONS = [
    {
        // Transparent-background dancing Felix. THE canonical loader — used
        // everywhere a "we're working on it" affordance appears, because the
        // baked-in dark/colored backgrounds on the other variants render a
        // visible square on light surfaces and on the route-suspense screen.
        id: 'helix-dancing-nobg',
        label: 'Helix Dancing (transparent)',
        src: '/loading/helix-dancing-nobg.gif',
        kind: 'gif',
        compositeMode: 'normal',
    },
    {
        id: 'felix-color',
        label: 'Felix Color',
        src: '/loading/felix-color-sm.gif',
        videoSrc: '/loading/felix-color.mp4',
        kind: 'gif',
        compositeMode: 'normal',
    },
    {
        id: 'felix-cyan',
        label: 'Felix Cyan',
        src: '/loading/felix-cyan-sm.gif',
        videoSrc: '/loading/felix-cyan.mp4',
        kind: 'gif',
        compositeMode: 'normal',
    },
    {
        id: 'felix-darkblue',
        label: 'Felix Dark Blue',
        src: '/loading/felix-darkblue-sm.gif',
        videoSrc: '/loading/felix-darkblue.mp4',
        kind: 'gif',
        compositeMode: 'normal',
    },
    {
        id: 'felix-white',
        label: 'Felix White',
        src: '/loading/felix-white-sm.gif',
        videoSrc: '/loading/felix-white.mp4',
        kind: 'gif',
        compositeMode: 'normal',
    },
    {
        id: 'felix-black',
        label: 'Felix Black',
        src: '/loading/felix-black-sm.gif',
        videoSrc: '/loading/felix-black.mp4',
        kind: 'gif',
        compositeMode: 'normal',
    },
    {
        id: 'helix-dancing',
        label: 'Helix Dancing (legacy)',
        src: '/loading/helix-dancing.gif',
        kind: 'gif',
        compositeMode: 'normal',
    },
];

export const DEFAULT_LOADING_ANIMATION = LOADING_ANIMATIONS[0]; // helix-dancing-nobg

// Surface defaults point at the transparent loader on every surface so the
// dark/colored squares of the legacy variants never show through. Override
// per-call by passing animationId="felix-cyan" etc. when a themed loader is
// explicitly wanted.
const SURFACE_DEFAULTS = {
    timeline: 'helix-dancing-nobg',
    canvas:   'helix-dancing-nobg',
};

/** Return the registry entry for `id`, or the default if not found / not provided. */
export function getLoadingAnimation(id) {
    if (!id) return DEFAULT_LOADING_ANIMATION;
    if (id === 'random') {
        return LOADING_ANIMATIONS[Math.floor(Math.random() * LOADING_ANIMATIONS.length)];
    }
    return LOADING_ANIMATIONS.find((a) => a.id === id) || DEFAULT_LOADING_ANIMATION;
}

/**
 * Return the right animation for a rendering surface.
 * @param {'timeline'|'canvas'|string} [surface]
 */
export function getAnimationForSurface(surface) {
    const id = SURFACE_DEFAULTS[surface];
    return id ? getLoadingAnimation(id) : DEFAULT_LOADING_ANIMATION;
}
